import { useCallback, useRef, useState } from 'react'

import type { RecordType, SurveyDatasets } from '@factorialco/f0-react'
import { useDataSource } from '@factorialco/f0-react'
import type { PurchaseRequestVendorSelectorQuery } from 'generated/resources/graphql'
import {
  PurchaseRequestCurrencySelectorDocument,
  PurchaseRequestVendorSelectorDocument,
} from 'generated/resources/graphql'

import { useQuery } from 'modules/core/hooks/useQuery'
import {
  createGraphqlDataSourceDefinition,
  useGraphqlDataSource,
} from 'modules/core/hooks/useGraphqlDataSource'
import i18n from 'modules/core/lib/i18n'

function useCurrencyDataSource() {
  const { data } = useQuery(PurchaseRequestCurrencySelectorDocument, {
    variables: { perPage: 200 },
    fetchPolicy: 'cache-first',
  })

  const allCurrencies: RecordType[] =
    data?.apiCore.currenciesConnection.edges.map((edge) => edge.node) ?? []

  return useDataSource(
    {
      search: { enabled: true },
      dataAdapter: {
        fetchData: ({ search }) => {
          const lower = search?.toLowerCase() ?? ''
          const records = lower
            ? allCurrencies.filter((c) => String(c.isoCode).toLowerCase().includes(lower))
            : allCurrencies
          return { records, total: records.length }
        },
      },
    },
    [allCurrencies]
  )
}

const createVendorSourceDefinition = (companyId: string) =>
  createGraphqlDataSourceDefinition({
    document: PurchaseRequestVendorSelectorDocument,
    paginationType: 'infinite-scroll',
    perPage: 20,
    search: { enabled: true },
    mapVariables: ({ search }) => ({ companyId, search: search || undefined }),
    mapData: (data: PurchaseRequestVendorSelectorQuery): RecordType[] =>
      data.finance.contactsConnection.edges.map((edge) => edge.node),
    mapPagination: (data: PurchaseRequestVendorSelectorQuery) => ({
      total: data.finance.contactsConnection.totalCount ?? 0,
      cursor: data.finance.contactsConnection.pageInfo.endCursor ?? undefined,
      hasNextPage: data.finance.contactsConnection.pageInfo.hasNextPage,
    }),
  })

export interface CreateVendorModalState {
  isOpen: boolean
  initialName: string
}

interface UsePurchaseRequestDatasetsOptions {
  enableVendorCreation?: boolean
}

export interface UsePurchaseRequestDatasetsResult {
  datasets: SurveyDatasets
  createVendorModal: CreateVendorModalState
  onVendorCreated: (vendor: RecordType) => void
  onCreateVendorModalClose: () => void
}

export function usePurchaseRequestDatasets(
  companyId: string,
  options: UsePurchaseRequestDatasetsOptions = {}
): UsePurchaseRequestDatasetsResult {
  const { enableVendorCreation = false } = options
  const currencyDataSource = useCurrencyDataSource()
  const vendorDataSource = useGraphqlDataSource(createVendorSourceDefinition(companyId), [
    companyId,
  ])

  const [createVendorModal, setCreateVendorModal] = useState<CreateVendorModalState>({
    isOpen: false,
    initialName: '',
  })

  const resolverRef = useRef<{
    resolve: (vendor: RecordType) => void
    reject: (reason?: unknown) => void
  } | null>(null)

  const handleCreateVendor = useCallback((name: string): Promise<RecordType> => {
    if (resolverRef.current) {
      resolverRef.current.reject(new Error('Cancelled: new create vendor request'))
      resolverRef.current = null
    }
    return new Promise<RecordType>((resolve, reject) => {
      resolverRef.current = { resolve, reject }
      setCreateVendorModal({ isOpen: true, initialName: name })
    })
  }, [])

  const onVendorCreated = useCallback((vendor: RecordType) => {
    resolverRef.current?.resolve(vendor)
    resolverRef.current = null
    setCreateVendorModal({ isOpen: false, initialName: '' })
  }, [])

  const onCreateVendorModalClose = useCallback(() => {
    resolverRef.current?.reject(new Error('User cancelled vendor creation'))
    resolverRef.current = null
    setCreateVendorModal({ isOpen: false, initialName: '' })
  }, [])

  const datasets: SurveyDatasets = {
    currency: {
      title: i18n.t('finance.currency'),
      dataSource: currencyDataSource,
      mapOptions: (item) => ({
        value: String(item.isoCode),
        label: String(item.isoCode),
      }),
    },
    vendor: {
      title: i18n.t(
        'procurement.purchase_requests.new_request_wizard.steps.general_information.fields.vendor'
      ),
      dataSource: vendorDataSource,
      mapOptions: (item) => ({
        value: String(item.id),
        label: String(item.legalName || item.name),
      }),
      ...(enableVendorCreation && { onCreate: handleCreateVendor }),
    },
  }

  return { datasets, createVendorModal, onVendorCreated, onCreateVendorModalClose }
}
